import { useState } from "react";
import { motion } from "framer-motion";
import {
  Video, BookOpen, Download, Calendar, Clock, Users,
  ChevronRight, FileText, Star, CheckCircle, ArrowRight, BookMarked
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  COURSES_LIVE, COURSES_RECORDED, MATERIALS_FREE, BLOG_POSTS
} from "@/data/index";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

// @section: home-page
export default function Home() {
  const [activeTab, setActiveTab] = useState<"directos" | "grabados">("directos");

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#ede8d0" }}>
      <Header />

      {/* =========================================================
          HERO
      ========================================================= */}
      {/* @section: hero */}
      <section
        id="inicio"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ paddingTop: "80px", backgroundColor: "#d6eef2" }}
      >
        <div className="relative z-10 container mx-auto px-4 md:px-8 text-center py-20">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="max-w-3xl mx-auto"
          >
            <motion.div variants={fadeUp} className="mb-6">
              <span
                className="inline-block text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full"
                style={{
                  backgroundColor: "rgba(201,151,42,0.18)",
                  color: "#c9972a",
                  border: "1px solid rgba(201,151,42,0.4)",
                }}
              >
                Formación docente · Lengua Castellana y Literatura
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="mb-6 leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
                fontWeight: 700,
                color: "#1e2d4e",
              }}
            >
              Proyecto Nebrija
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed"
              style={{ color: "rgba(30,45,78,0.72)" }}
            >
              Recursos, materiales y cursos de Lengua Castellana y Literatura
              para docentes y estudiantes. Acceso gratuito a cientos de materiales.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => scrollTo("cursos")}
                className="font-semibold px-8 text-white hover:opacity-90"
                style={{ backgroundColor: "#c9972a" }}
              >
                Ver cursos disponibles
                <ChevronRight size={18} className="ml-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollTo("materiales")}
                className="font-semibold px-8"
                style={{
                  borderColor: "rgba(30,45,78,0.35)",
                  color: "#1e2d4e",
                  backgroundColor: "transparent",
                }}
              >
                Materiales gratuitos
              </Button>
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="mt-14 grid grid-cols-3 gap-4 max-w-sm mx-auto"
            >
              {[
                { value: "+500", label: "Materiales" },
                { value: "+1.200", label: "Docentes" },
                { value: "100%", label: "Gratis" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <p
                    className="text-2xl font-bold"
                    style={{ fontFamily: "'Playfair Display', serif", color: "#c9972a" }}
                  >
                    {s.value}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: "rgba(30,45,78,0.6)" }}>
                    {s.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          SOBRE MÍ
      ========================================================= */}
      {/* @section: about */}
      <section id="sobre-mi" className="py-20 md:py-28" style={{ backgroundColor: "#f5f2e8" }}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div
                className="rounded-2xl overflow-hidden aspect-[4/5] shadow-lg"
                style={{ border: "3px solid #c9972a" }}
              >
                <img
                  src="/images/edu_1.jpeg"
                  alt="Foto docente Proyecto Nebrija"
                  className="w-full h-full object-cover"
                />
              </div>
              <div
                className="absolute -bottom-4 -right-4 px-4 py-2 rounded-xl shadow-md text-sm font-semibold"
                style={{ backgroundColor: "#b3ebf2", color: "#1e2d4e", border: "1px solid rgba(30,45,78,0.15)" }}
              >
                Docente · Región de Murcia
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            >
              <span
                className="text-xs font-semibold tracking-widest uppercase"
                style={{ color: "#c9972a" }}
              >
                Sobre mí
              </span>
              <h2
                className="mt-2 mb-5 leading-tight"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                  color: "#1e2d4e",
                }}
              >
                Docente de Lengua Castellana y Literatura
              </h2>
              <p className="text-sm leading-relaxed mb-4" style={{ color: "#4a4a4a" }}>
                Soy profesor/a de Lengua Castellana y Literatura en ESO y Bachillerato
                en la Región de Murcia, funcionario/a de carrera y apasionado/a de la
                gramática española. Corrector/a de la PAU de Lengua en Murcia.
              </p>
              <p className="text-sm leading-relaxed mb-6" style={{ color: "#4a4a4a" }}>
                He creado este espacio para compartir recursos educativos de calidad y
                ofrecer formación actualizada a otros docentes. Mi lema: compartir el
                conocimiento es la mejor forma de hacerlo crecer.
              </p>

              <ul className="flex flex-col gap-2 mb-8">
                {[
                  "Corrector/a de la PAU de Lengua · Región de Murcia",
                  "Formador/a de profesores de Lengua desde 2024",
                  "Seguimiento de la NGLE y GTG (2019)",
                  "Criterios PAU Región de Murcia",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: "#4a4a4a" }}
                  >
                    <CheckCircle
                      size={16}
                      className="mt-0.5 shrink-0"
                      style={{ color: "#c9972a" }}
                    />
                    {item}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => scrollTo("contacto")}
                className="font-semibold"
                style={{ backgroundColor: "#b3ebf2", color: "#1e2d4e" }}
              >
                Contactar
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* =========================================================
          CURSOS
      ========================================================= */}
      {/* @section: courses */}
      <section id="cursos" className="py-20 md:py-28" style={{ backgroundColor: "#ede8d0" }}>
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: "#c9972a" }}
            >
              Formación
            </span>
            <h2
              className="mt-2 mb-3"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
                color: "#1e2d4e",
              }}
            >
              Cursos disponibles
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: "#5a5a5a" }}>
              Formación especializada en Lengua Castellana y Literatura: sintaxis,
              morfología, comentario de texto y preparación de oposiciones.
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center mb-10">
            <div
              className="flex rounded-lg p-1 gap-1"
              style={{ backgroundColor: "rgba(30,45,78,0.08)" }}
            >
              {[
                { key: "directos", label: "En Directo", icon: <Video size={14} /> },
                { key: "grabados", label: "Grabados", icon: <BookOpen size={14} /> },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as "directos" | "grabados")}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-md text-sm font-medium transition-all duration-200"
                  style={
                    activeTab === tab.key
                      ? {
                          backgroundColor: "#b3ebf2",
                          color: "#1e2d4e",
                          boxShadow: "0 2px 8px rgba(179,235,242,0.5)",
                        }
                      : { color: "#1e2d4e" }
                  }
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Course cards */}
          <motion.div
            key={activeTab}
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          >
            {(activeTab === "directos" ? COURSES_LIVE : COURSES_RECORDED).map((course) => (
              <motion.div key={course.id} variants={fadeUp}>
                <Card
                  className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  style={{
                    backgroundColor: "#f5f2e8",
                    border: "1px solid rgba(30,45,78,0.12)",
                  }}
                >
                  <div className="h-1" style={{ backgroundColor: "#b3ebf2" }} />
                  <CardContent className="p-5 flex flex-col flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <Badge
                        className="text-xs font-semibold"
                        style={{ backgroundColor: "rgba(30,45,78,0.08)", color: "#1e2d4e" }}
                      >
                        {activeTab === "directos" ? "En Directo" : "Grabado"}
                      </Badge>
                      <span
                        className="text-base font-bold"
                        style={{
                          color: "#c9972a",
                          fontFamily: "'Playfair Display', serif",
                        }}
                      >
                        {course.price} €
                      </span>
                    </div>

                    <h3
                      className="text-sm font-semibold mb-2 leading-snug"
                      style={{
                        color: "#1e2d4e",
                        fontFamily: "'Playfair Display', serif",
                      }}
                    >
                      {course.title}
                    </h3>
                    <p
                      className="text-xs leading-relaxed mb-4 flex-1"
                      style={{ color: "#5a5a5a" }}
                    >
                      {course.description}
                    </p>

                    <div className="flex flex-col gap-1.5 mb-4">
                      {"date" in course && (
                        <div
                          className="flex items-center gap-1.5 text-xs"
                          style={{ color: "#5a5a5a" }}
                        >
                          <Calendar size={13} style={{ color: "#c9972a" }} />
                          {course.date}
                        </div>
                      )}
                      <div
                        className="flex items-center gap-1.5 text-xs"
                        style={{ color: "#5a5a5a" }}
                      >
                        <Clock size={13} style={{ color: "#c9972a" }} />
                        {course.duration}
                      </div>
                      {"spots" in course && (
                        <div
                          className="flex items-center gap-1.5 text-xs"
                          style={{ color: "#5a5a5a" }}
                        >
                          <Users size={13} style={{ color: "#c9972a" }} />
                          {course.spots} plazas disponibles
                        </div>
                      )}
                      {"lessons" in course && (
                        <div
                          className="flex items-center gap-1.5 text-xs"
                          style={{ color: "#5a5a5a" }}
                        >
                          <BookOpen size={13} style={{ color: "#c9972a" }} />
                          {course.lessons} lecciones
                        </div>
                      )}
                    </div>

                    <Button
                      size="sm"
                      className="w-full font-semibold"
                      style={{ backgroundColor: "#b3ebf2", color: "#1e2d4e" }}
                    >
                      Ver detalles
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Aula opositores banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 max-w-5xl mx-auto rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6"
            style={{ backgroundColor: "#b3ebf2" }}
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star size={16} style={{ color: "#c9972a" }} />
                <span
                  className="text-xs font-semibold tracking-widest uppercase"
                  style={{ color: "#c9972a" }}
                >
                  Pack completo
                </span>
              </div>
              <h3
                className="text-xl font-bold mb-1"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: "#1e2d4e",
                }}
              >
                Aula de Opositores
              </h3>
              <p className="text-sm" style={{ color: "rgba(30,45,78,0.7)" }}>
                Acceso completo a todos los cursos de preparación de oposiciones.
              </p>
            </div>
            <Button
              size="lg"
              className="shrink-0 font-semibold text-white hover:opacity-90"
              style={{ backgroundColor: "#c9972a" }}
            >
              Ver el pack completo
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          MATERIALES GRATUITOS
      ========================================================= */}
      {/* @section: materials */}
      <section id="materiales" className="py-20 md:py-28" style={{ backgroundColor: "#f5f2e8" }}>
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: "#c9972a" }}
            >
              Descarga libre
            </span>
            <h2
              className="mt-2 mb-3"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
                color: "#1e2d4e",
              }}
            >
              Materiales gratuitos
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: "#5a5a5a" }}>
              Recursos educativos de libre acceso: dosieres, actividades competenciales,
              esquemas y artículos de opinión para el aula.
            </p>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto"
          >
            {MATERIALS_FREE.map((mat) => (
              <motion.div key={mat.id} variants={fadeUp}>
                <Card
                  className="hover:shadow-md transition-shadow duration-300 cursor-pointer group"
                  style={{
                    backgroundColor: "#fff",
                    border: "1px solid rgba(30,45,78,0.1)",
                  }}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div
                      className="shrink-0 w-11 h-11 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: "rgba(201,151,42,0.12)" }}
                    >
                      <FileText size={20} style={{ color: "#c9972a" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge
                          className="text-[10px]"
                          style={{ backgroundColor: "rgba(30,45,78,0.08)", color: "#1e2d4e" }}
                        >
                          {mat.type}
                        </Badge>
                        <span className="text-[10px]" style={{ color: "#aaa" }}>
                          {mat.pages} páginas
                        </span>
                        <span className="text-[10px]" style={{ color: "#aaa" }}>
                          ↓ {mat.downloadCount.toLocaleString("es")}
                        </span>
                      </div>
                      <h3
                        className="text-sm font-semibold mb-1 leading-snug"
                        style={{ color: "#1e2d4e" }}
                      >
                        {mat.title}
                      </h3>
                      <p className="text-xs leading-relaxed" style={{ color: "#666" }}>
                        {mat.description}
                      </p>
                    </div>
                    <div
                      className="shrink-0 p-2 rounded-lg transition-colors duration-200"
                      style={{ backgroundColor: "rgba(30,45,78,0.06)" }}
                    >
                      <Download size={16} style={{ color: "#1e2d4e" }} />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => scrollTo("registro")}
              className="font-semibold"
              style={{ borderColor: "#1e2d4e", color: "#1e2d4e" }}
            >
              Ver todos los materiales
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* =========================================================
          BLOG
      ========================================================= */}
      {/* @section: blog */}
      <section id="blog" className="py-20 md:py-28" style={{ backgroundColor: "#ede8d0" }}>
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4"
          >
            <div>
              <span
                className="text-xs font-semibold tracking-widest uppercase"
                style={{ color: "#c9972a" }}
              >
                Novedades
              </span>
              <h2
                className="mt-2"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
                  color: "#1e2d4e",
                }}
              >
                Últimas entradas del blog
              </h2>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 font-medium"
              style={{ borderColor: "#1e2d4e", color: "#1e2d4e" }}
            >
              Ver todo el blog
              <ArrowRight size={14} className="ml-1" />
            </Button>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {BLOG_POSTS.map((post) => (
              <motion.div key={post.id} variants={fadeUp}>
                <Card
                  className="h-full flex flex-col cursor-pointer group hover:shadow-md transition-shadow duration-300 overflow-hidden"
                  style={{
                    backgroundColor: "#f5f2e8",
                    border: "1px solid rgba(30,45,78,0.1)",
                  }}
                >
                  <div
                    className="h-1.5"
                    style={{
                      backgroundColor:
                        post.id === 1 ? "#c9972a" : post.id === 2 ? "#b3ebf2" : "#6b7c9a",
                    }}
                  />
                  <CardContent className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge
                        className="text-[10px] font-medium"
                        style={{ backgroundColor: "rgba(30,45,78,0.08)", color: "#1e2d4e" }}
                      >
                        {post.category}
                      </Badge>
                      <span className="text-[10px]" style={{ color: "#999" }}>
                        {post.date}
                      </span>
                    </div>
                    <h3
                      className="text-sm font-semibold mb-2 leading-snug group-hover:underline underline-offset-2"
                      style={{
                        color: "#1e2d4e",
                        fontFamily: "'Playfair Display', serif",
                      }}
                    >
                      {post.title}
                    </h3>
                    <p
                      className="text-xs leading-relaxed flex-1 mb-4"
                      style={{ color: "#666" }}
                    >
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px]" style={{ color: "#aaa" }}>
                        <Clock size={11} className="inline mr-0.5" />
                        {post.readTime} de lectura
                      </span>
                      <span
                        className="text-xs font-medium flex items-center gap-0.5"
                        style={{ color: "#c9972a" }}
                      >
                        Leer más <ChevronRight size={13} />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          REGISTRO CTA
      ========================================================= */}
      {/* @section: register-cta */}
      <section
        id="registro"
        className="py-20 md:py-28"
        style={{ backgroundColor: "#b3ebf2" }}
      >
        <div className="container mx-auto px-4 md:px-8 text-center max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6"
              style={{ backgroundColor: "rgba(201,151,42,0.18)" }}
            >
              <BookMarked size={26} style={{ color: "#c9972a" }} />
            </div>
            <h2
              className="mb-4 leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
                color: "#1e2d4e",
              }}
            >
              Regístrate y accede gratis
            </h2>
            <p
              className="text-sm mb-8 leading-relaxed"
              style={{ color: "rgba(30,45,78,0.72)" }}
            >
              Crea tu cuenta gratuita y descarga todos los materiales, accede a los
              contenidos exclusivos y mantente al día de nuevas publicaciones y cursos.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
              <Button
                size="lg"
                className="font-semibold px-8 text-white hover:opacity-90"
                style={{ backgroundColor: "#c9972a" }}
              >
                Crear cuenta gratuita
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="font-semibold px-8"
                style={{
                  borderColor: "rgba(30,45,78,0.35)",
                  color: "#1e2d4e",
                  backgroundColor: "transparent",
                }}
              >
                Ya tengo cuenta
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              {[
                "Acceso a materiales gratuitos",
                "Boletín de novedades",
                "Descuentos en cursos",
              ].map((item) => (
                <span
                  key={item}
                  className="flex items-center gap-1.5 text-xs"
                  style={{ color: "rgba(30,45,78,0.65)" }}
                >
                  <CheckCircle size={13} style={{ color: "#c9972a" }} />
                  {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* =========================================================
          CONTACTO
      ========================================================= */}
      {/* @section: contact */}
      <section id="contacto" className="py-20 md:py-28" style={{ backgroundColor: "#f5f2e8" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: "#c9972a" }}
            >
              Contacto
            </span>
            <h2
              className="mt-2 mb-3"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
                color: "#1e2d4e",
              }}
            >
              ¿Tienes alguna pregunta?
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: "#5a5a5a" }}>
              Escríbeme para cualquier consulta sobre cursos, colaboraciones o recursos.
              Respondo en menos de 48 horas.
            </p>
          </motion.div>

          <form className="flex flex-col gap-4 text-left">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#1e2d4e" }}
                >
                  Nombre
                </label>
                <input
                  type="text"
                  placeholder="Tu nombre"
                  className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                  style={{
                    border: "1px solid rgba(30,45,78,0.2)",
                    backgroundColor: "#fff",
                    color: "#1e2d4e",
                  }}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: "#1e2d4e" }}
                >
                  Correo electrónico
                </label>
                <input
                  type="email"
                  placeholder="tu@correo.es"
                  className="w-full rounded-lg px-4 py-2.5 text-sm outline-none"
                  style={{
                    border: "1px solid rgba(30,45,78,0.2)",
                    backgroundColor: "#fff",
                    color: "#1e2d4e",
                  }}
                />
              </div>
            </div>
            <div>
              <label
                className="block text-xs font-medium mb-1"
                style={{ color: "#1e2d4e" }}
              >
                Mensaje
              </label>
              <textarea
                placeholder="¿En qué puedo ayudarte?"
                rows={4}
                className="w-full rounded-lg px-4 py-2.5 text-sm outline-none resize-none"
                style={{
                  border: "1px solid rgba(30,45,78,0.2)",
                  backgroundColor: "#fff",
                  color: "#1e2d4e",
                }}
              />
            </div>
            <div className="text-center">
              <Button
                type="submit"
                className="font-semibold px-10"
                style={{ backgroundColor: "#b3ebf2", color: "#1e2d4e" }}
              >
                Enviar mensaje
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
